import { useState } from 'react';
import { cn } from '../lib/utils';
import { X, Download, ExternalLink, ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';
import {
  Dialog,
  DialogContent,
} from './ui/dialog';

// Full-screen media viewer for images and videos
export function MediaViewer({ 
  open, 
  onOpenChange, 
  mediaUrl, 
  mediaType = 'image', 
  title = 'Media',
  allMedia = [], // Optional: for gallery navigation
  currentIndex = 0,
  onNavigate // Optional: callback for gallery navigation
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  
  const handleDownload = async () => {
    try {
      const response = await fetch(mediaUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const extension = mediaType === 'video' ? 'mp4' : 'jpg';
      link.download = `${title.replace(/\s+/g, '_')}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Fallback: open in new tab for download
      window.open(mediaUrl, '_blank');
    }
  };

  const canNavigate = allMedia.length > 1 && onNavigate;
  
  const handlePrev = () => {
    if (canNavigate) {
      const newIndex = currentIndex === 0 ? allMedia.length - 1 : currentIndex - 1;
      onNavigate(newIndex);
    }
  };
  
  const handleNext = () => {
    if (canNavigate) {
      const newIndex = currentIndex === allMedia.length - 1 ? 0 : currentIndex + 1;
      onNavigate(newIndex);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[95vh] p-0 bg-black/95 border-none">
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4 bg-gradient-to-b from-black/80 to-transparent">
          <h3 className="text-white font-medium truncate max-w-md">{title}</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              title="Download"
            >
              <Download className="w-5 h-5 text-white" />
            </button>
            <a
              href={mediaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              title="Open in new tab"
            >
              <ExternalLink className="w-5 h-5 text-white" />
            </a>
            <button
              onClick={() => onOpenChange(false)}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Media Content */}
        <div className="flex items-center justify-center min-h-[70vh] p-8 pt-16">
          {mediaType === 'video' ? (
            <video
              src={mediaUrl}
              controls
              autoPlay
              className="max-w-full max-h-[75vh] rounded-lg"
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />
          ) : (
            <img
              src={mediaUrl}
              alt={title}
              className="max-w-full max-h-[75vh] object-contain rounded-lg"
            />
          )}
        </div>

        {/* Navigation Arrows (for galleries) */}
        {canNavigate && (
          <>
            <button
              onClick={handlePrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/50 rounded-full text-white text-sm">
              {currentIndex + 1} / {allMedia.length}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Clickable media card with view/download options
export function MediaCard({ 
  url, 
  type = 'image', 
  label,
  onView,
  className,
  aspectRatio = 'aspect-square'
}) {
  const handleDownload = async (e) => {
    e.stopPropagation();
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      const extension = type === 'video' ? 'mp4' : 'jpg';
      link.download = `${label || 'media'}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      window.open(url, '_blank');
    }
  };

  if (!url) {
    return (
      <div className={cn(aspectRatio, 'bg-slate-100 rounded-lg flex items-center justify-center', className)}>
        <div className="text-center">
          {type === 'video' ? (
            <Play className="w-8 h-8 text-slate-300 mx-auto mb-1" />
          ) : (
            <div className="w-8 h-8 rounded bg-slate-200 mx-auto mb-1" />
          )}
          <p className="text-xs text-slate-400">{label || 'No media'}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={cn(
        aspectRatio, 
        'bg-slate-100 rounded-lg overflow-hidden relative group cursor-pointer',
        className
      )}
      onClick={() => onView && onView()}
    >
      {type === 'video' ? (
        <video
          src={url}
          className="w-full h-full object-cover"
          preload="metadata"
        />
      ) : (
        <img
          src={url}
          alt={label || 'Media'}
          className="w-full h-full object-cover"
        />
      )}
      
      {/* Hover Overlay */}
      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
        <button
          onClick={(e) => { e.stopPropagation(); onView && onView(); }}
          className="p-2 bg-white rounded-full hover:bg-cyan-50 transition-colors"
          title="View"
        >
          {type === 'video' ? (
            <Play className="w-5 h-5 text-cyan-600" />
          ) : (
            <ExternalLink className="w-5 h-5 text-cyan-600" />
          )}
        </button>
        <button
          onClick={handleDownload}
          className="p-2 bg-white rounded-full hover:bg-cyan-50 transition-colors"
          title="Download"
        >
          <Download className="w-5 h-5 text-cyan-600" />
        </button>
      </div>
      
      {/* Play icon for videos */}
      {type === 'video' && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none group-hover:opacity-0 transition-opacity">
          <div className="w-12 h-12 bg-black/50 rounded-full flex items-center justify-center">
            <Play className="w-6 h-6 text-white ml-1" />
          </div>
        </div>
      )}
    </div>
  );
}

// Video card specifically for UGC videos
export function VideoCard({ url, label, onView }) {
  return (
    <MediaCard 
      url={url} 
      type="video" 
      label={label} 
      onView={onView}
      aspectRatio="aspect-video"
    />
  );
}

// Image card for static ads
export function ImageCard({ url, label, onView }) {
  return (
    <MediaCard 
      url={url} 
      type="image" 
      label={label} 
      onView={onView}
      aspectRatio="aspect-square"
    />
  );
}
